import React, { useState, useEffect } from "react";
import {
    makeStyles,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Box,
    Card,
    CardContent,
    Divider,
    Grid
} from "@material-ui/core";
import { toast } from "react-toastify";
import QrCodeIcon from "@material-ui/icons/CropFree";
import GetAppIcon from "@material-ui/icons/GetApp";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import InfoIcon from "@material-ui/icons/Info";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(3),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    contentWrapper: {
        maxWidth: 900,
        margin: "0 auto",
    },
    card: {
        marginBottom: theme.spacing(3),
    },
    textField: {
        marginBottom: theme.spacing(2),
    },
    generateButton: {
        marginTop: theme.spacing(2),
        backgroundColor: "#25D366",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#128C7E",
        },
    },
    previewContainer: {
        marginTop: theme.spacing(3),
        textAlign: "center",
    },
    previewImage: {
        maxWidth: "100%",
        height: "auto",
        borderRadius: theme.spacing(1),
        boxShadow: theme.shadows[3],
    },
    downloadButton: {
        marginTop: theme.spacing(2),
        backgroundColor: "#075E54",
        color: "#fff",
        "&:hover": {
            backgroundColor: "#054d44",
        },
    },
    infoCard: {
        backgroundColor: "#E3F2FD",
        marginTop: theme.spacing(3),
    },
    infoIcon: {
        color: "#1976D2",
        marginRight: theme.spacing(1),
    },
    whatsappNumber: {
        color: "#25D366",
        fontWeight: "bold",
    },
}));

const WhatsAppQRCode = () => {
    const classes = useStyles();
    const [messageText, setMessageText] = useState("Quero me cadastrar!");
    const [whatsappNumber, setWhatsappNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingNumber, setLoadingNumber] = useState(true);
    const [qrCodeImage, setQrCodeImage] = useState(null);

    useEffect(() => {
        fetchWhatsAppNumber();
    }, []);

    const fetchWhatsAppNumber = async () => {
        try {
            setLoadingNumber(true);
            const { data } = await api.get("/whatsapp-qrcode/number");
            setWhatsappNumber(data.number);
        } catch (err) {
            console.error("Erro ao buscar número do WhatsApp:", err);
            toast.error("Erro ao buscar número do WhatsApp conectado");
        } finally {
            setLoadingNumber(false);
        }
    };

    const handleGenerateQRCode = async () => {
        if (!messageText.trim()) {
            toast.warning("Por favor, digite uma mensagem");
            return;
        }

        try {
            setLoading(true);
            const response = await api.post(
                "/whatsapp-qrcode/generate",
                { text: messageText },
                {
                    responseType: "blob",
                }
            );

            // Cria URL do blob para preview
            const imageUrl = URL.createObjectURL(response.data);
            setQrCodeImage(imageUrl);
            toast.success("QR Code gerado com sucesso!");
        } catch (err) {
            console.error("Erro ao gerar QR Code:", err);
            toast.error("Erro ao gerar QR Code");
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (qrCodeImage) {
            const link = document.createElement("a");
            link.href = qrCodeImage;
            link.download = "qrcode-whatsapp-promocoes.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            toast.success("Download iniciado!");
        }
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>QR Code WhatsApp</Title>
                <MainHeaderButtonsWrapper></MainHeaderButtonsWrapper>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <div className={classes.contentWrapper}>
                    <Card className={classes.card}>
                        <CardContent>
                            <Box display="flex" alignItems="center" mb={2}>
                                <WhatsAppIcon style={{ color: "#25D366", marginRight: 8 }} />
                                <Typography variant="h6">
                                    Gerar QR Code para WhatsApp
                                </Typography>
                            </Box>
                            <Divider />
                            <Box mt={3}>
                                <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Número do WhatsApp conectado:
                                </Typography>
                                {loadingNumber ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Typography variant="h6" className={classes.whatsappNumber}>
                                        {whatsappNumber || "Nenhum número conectado"}
                                    </Typography>
                                )}

                                <TextField
                                    label="Mensagem pré-preenchida"
                                    variant="outlined"
                                    fullWidth
                                    multiline
                                    rows={3}
                                    value={messageText}
                                    onChange={(e) => setMessageText(e.target.value)}
                                    className={classes.textField}
                                    helperText="Esta mensagem será pré-preenchida quando o usuário escanear o QR Code"
                                    style={{ marginTop: 20 }}
                                />

                                <Button
                                    variant="contained"
                                    fullWidth
                                    size="large"
                                    className={classes.generateButton}
                                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <QrCodeIcon />}
                                    onClick={handleGenerateQRCode}
                                    disabled={loading || !whatsappNumber}
                                >
                                    {loading ? "Gerando..." : "Gerar QR Code"}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>

                    {qrCodeImage && (
                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Preview do QR Code
                                </Typography>
                                <Divider />
                                <div className={classes.previewContainer}>
                                    <img
                                        src={qrCodeImage}
                                        alt="QR Code WhatsApp"
                                        className={classes.previewImage}
                                    />
                                    <Button
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        className={classes.downloadButton}
                                        startIcon={<GetAppIcon />}
                                        onClick={handleDownload}
                                    >
                                        Baixar Imagem
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card className={classes.infoCard}>
                        <CardContent>
                            <Box display="flex" alignItems="flex-start">
                                <InfoIcon className={classes.infoIcon} />
                                <div>
                                    <Typography variant="subtitle1" gutterBottom>
                                        <strong>Como usar:</strong>
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        1. Digite a mensagem que deseja pré-preencher no WhatsApp
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        2. Clique em "Gerar QR Code" para criar a imagem
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        3. Baixe a imagem e use em materiais impressos (flyers, cartões, banners)
                                    </Typography>
                                    <Typography variant="body2" paragraph>
                                        4. Quando alguém escanear o QR Code, o WhatsApp abrirá automaticamente com sua mensagem
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        <strong>Dica:</strong> Use mensagens curtas e diretas para melhor conversão!
                                    </Typography>
                                </div>
                            </Box>
                        </CardContent>
                    </Card>
                </div>
            </Paper>
        </MainContainer>
    );
};

export default WhatsAppQRCode;
